import { useRef, useState } from "react";
import { Button } from "@humansignal/ui";
import { Modal } from "../../components/Modal/Modal";

/**
 * ProjectLogoPicker — the same two-tab experience as the profile avatar
 * picker: pick from a gallery or upload your own. Inside the gallery, the
 * chips select an icon TYPE (like the avatar picker's style chips), each a
 * different DiceBear style. Every tile is a generated, deterministic icon
 * (seed → same icon), so a grid gives distinct colourful options per type.
 *
 * Both paths resolve to a real uploaded image: a gallery pick fetches the
 * PNG and hands it up as a File, so the server stores one blob either way.
 *
 * Tiles are served locally from Django static
 * (``/static/project-logos/<type>-<n>.png``, generated with DiceBear and
 * downloaded), so there's no external request / CSP issue and it works
 * offline. To refresh or extend the set, re-run the download into that dir.
 */
// Project-appropriate icon types only. Abstract/generative marks (DiceBear:
// shapes, glass, rings, identicon, icons) plus a curated topic-emoji set
// (Twemoji). Avatar-like styles (bots, faces, pixel people) live in the
// profile avatar picker instead.
const TYPES: ReadonlyArray<{ key: string; label: string }> = [
  { key: "shapes", label: "Shapes" },
  { key: "glass", label: "Glass" },
  { key: "rings", label: "Rings" },
  { key: "identicon", label: "Marks" },
  { key: "icons", label: "Icons" },
  { key: "emoji", label: "Topics" },
];
const OPTIONS_PER_TYPE = 24;
const iconPath = (type: string, n: number) => `/static/project-logos/${type}-${n}.png`;

const chipBase: React.CSSProperties = {
  border: "1px solid var(--color-neutral-border)",
  background: "var(--color-neutral-background)",
  color: "var(--color-neutral-content)",
  borderRadius: 999,
  padding: "5px 12px",
  fontSize: "var(--font-size-caption)",
  fontWeight: 600,
  cursor: "pointer",
};
const chipActive: React.CSSProperties = {
  background: "var(--color-primary-emphasis-subtle, var(--color-primary-background))",
  borderColor: "var(--color-primary-border, var(--color-primary-content))",
  color: "var(--color-primary-content)",
};
const tileStyle: React.CSSProperties = {
  width: "100%",
  aspectRatio: "1 / 1",
  borderRadius: 10,
  overflow: "hidden",
  border: "1px solid var(--color-neutral-border)",
  background: "var(--color-neutral-background)",
  cursor: "pointer",
  padding: 0,
};

export const ProjectLogoPicker = ({
  open,
  onClose,
  onPick,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  /** Selected image (gallery or upload) as a real File the parent uploads. */
  onPick: (file: File) => Promise<void> | void;
  busy: boolean;
}) => {
  const [tab, setTab] = useState<"gallery" | "upload">("gallery");
  const [typeKey, setTypeKey] = useState(TYPES[0].key);
  const fileRef = useRef<HTMLInputElement | null>(null);

  if (!open) return null;
  const current = TYPES.find((s) => s.key === typeKey) ?? TYPES[0];

  const pickIcon = async (type: string, n: number) => {
    if (busy) return;
    try {
      const resp = await fetch(iconPath(type, n));
      const blob = await resp.blob();
      await onPick(new File([blob], `${type}-${n}.png`, { type: blob.type || "image/png" }));
    } catch {
      /* parent surfaces the error toast */
    }
  };

  const tabBtn = (k: "gallery" | "upload", label: string) => (
    <button
      key={k}
      type="button"
      role="tab"
      aria-selected={tab === k}
      onClick={() => setTab(k)}
      style={{
        border: 0,
        background: "transparent",
        padding: "8px 12px",
        marginBottom: -1,
        cursor: "pointer",
        fontSize: "var(--font-size-body-small)",
        fontWeight: tab === k ? 600 : 500,
        color: tab === k ? "var(--color-primary-content)" : "var(--color-neutral-content)",
        borderBottom: tab === k ? "2px solid var(--color-primary-content)" : "2px solid transparent",
      }}
    >
      {label}
    </button>
  );

  return (
    <Modal
      visible
      onHide={onClose}
      title="Project logo"
      style={{ width: 640 }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="negative" look="outlined" size="small" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </div>
      }
    >
      <div>
        <div
          style={{
            display: "flex",
            gap: 4,
            borderBottom: "1px solid var(--color-neutral-border-subtle)",
            marginBottom: 16,
          }}
          role="tablist"
        >
          {tabBtn("gallery", "Pick a logo")}
          {tabBtn("upload", "Upload your own")}
        </div>

        {/* Fixed panel height so the modal is the same size on both tabs. */}
        <div style={{ minHeight: 392 }}>
          {tab === "gallery" ? (
            <div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                {TYPES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setTypeKey(s.key)}
                    style={{ ...chipBase, ...(s.key === typeKey ? chipActive : {}) }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))",
                  gap: 10,
                  maxHeight: 340,
                  overflowY: "auto",
                }}
              >
                {Array.from({ length: OPTIONS_PER_TYPE }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={busy}
                    onClick={() => pickIcon(current.key, n)}
                    title={`${current.label} ${n}`}
                    style={tileStyle}
                  >
                    <img
                      src={iconPath(current.key, n)}
                      alt=""
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        display: "block",
                        padding: 4,
                        boxSizing: "border-box",
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                alignItems: "flex-start",
                padding: "12px 0 20px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--font-size-body-small)",
                  color: "var(--color-neutral-content-subtle)",
                }}
              >
                JPG, PNG or GIF, up to 1 MB. Square works best.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  e.currentTarget.value = "";
                  if (f) onPick(f);
                }}
              />
              <Button look="outlined" size="small" disabled={busy} onClick={() => fileRef.current?.click()}>
                {busy ? "Uploading…" : "Choose file"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProjectLogoPicker;
